import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppSelector } from 'app/hooks';
import { getPsyPopupState } from './psychrometricStorage';

/**
 * Dev-sync J1 — the HOST state machine of the psychrometric chart popup.
 *
 * React port of the three header.js members that own the popup's lifecycle
 * (modeled on the Phase 6.5 useIndependentRating.ts precedent):
 *
 *  - `openPsychrometricChart` (header.js:555-563):
 *      if (self.psy_popup) { self.psy_popup.restore(); return; }   ← EARLY RETURN
 *      self.psy_popup = new ViewPsychrometricChart();
 *      $("body").append(self.psy_popup.$el); self.psy_popup.show();
 *    → create-once/never-destroy: `mounted` never returns to false; every
 *    click after the first is a restore() (here: an openNonce bump the popup
 *    consumes).
 *
 *  - the page-load minimize replay (header.js:250-266): if
 *    `psy_popup_state === 'minimize'` and app_name === 'ahu', construct the
 *    view, renderShell (NO data fetch) and minimize() — the floating icon
 *    reappears without paying for the chart. → `boot: 'minimized'`.
 *    ★ Unlike the Independent Rating key there is NO auto-reopen on 'open'
 *    and NO cross-tab storage listener — mirrored by not implementing them.
 *
 *  - the enable gate (`unit.js:779`): `#psychrometric_chart_button` renders
 *    `non-clickable` (header.tmpl:121) and is enabled by
 *    `deActivateValidating` — the unit's validation-complete hook, the same
 *    one that enables `#delete_section`. Once enabled it is never re-disabled
 *    (activateValidating does not re-add the class to this button). React
 *    signal: the ahuValidation slice bumps `revision` when validation
 *    results land (validate_all_3D boot kick → setAllErrors), so a one-way
 *    latch on `revision > 0` reproduces disabled-until-first-validation.
 */
export interface PsychrometricsHost {
  /** true once the popup has been constructed — never returns to false. */
  mounted: boolean;
  /** why the mount exists: 'show' (button) or 'minimized' (page-load replay). */
  boot: 'show' | 'minimized';
  /** bumped per header-button click after the first — the popup's restore(). */
  openNonce: number;
  /** the header button's non-clickable gate (unit.js:779 latch). */
  enabled: boolean;
  /** header.js:555 — the #psychrometric_chart_button click. */
  open: () => void;
}

export function usePsychrometrics(): PsychrometricsHost {
  const [mounted, setMounted] = useState(false);
  const [boot, setBoot] = useState<'show' | 'minimized'>('show');
  const [openNonce, setOpenNonce] = useState(0);
  /** synchronous mirror of `mounted` — the legacy check is a live member
   * read (`if (self.psy_popup)`). */
  const mountedRef = useRef(false);

  // The one-way enable latch (see header doc above).
  const revision = useAppSelector((s) => s.ahuValidation.revision);
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    if (revision > 0) setEnabled(true);
  }, [revision]);

  const open = useCallback(() => {
    if (mountedRef.current) {
      // header.js:556-559 — already constructed: restore(), return.
      setOpenNonce((n) => n + 1);
      return;
    }
    mountedRef.current = true;
    setBoot('show');
    setMounted(true);
  }, []);

  // Page-load minimize replay (header.js:250-266) — shell only, no fetch.
  useEffect(() => {
    if (mountedRef.current) return;
    if (getPsyPopupState() === 'minimize') {
      mountedRef.current = true;
      setBoot('minimized');
      setMounted(true);
    }
  }, []);

  return { mounted, boot, openNonce, enabled, open };
}
